import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import { useSelector } from 'react-redux'
import { SALES_APPROVAL_PERMISSION } from '../Components/Permission'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d && d.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  if (obj.data && typeof obj.data === 'object' && typeof obj.data.data === 'string') {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) }
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function pickSaleFromResponse(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (dataBlock.sale && typeof dataBlock.sale === 'object') {
    return dataBlock.sale
  }

  if (dataBlock.sales && Array.isArray(dataBlock.sales) && dataBlock.sales.length > 0) {
    return dataBlock.sales[0]
  }

  if (dataBlock._id != null || dataBlock.companyName != null) {
    return dataBlock
  }

  return null
}

function displayUser(ref) {
  if (!ref) return '-'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  if (ref.email) return ref.email
  return '-'
}

/** Normalize Mongo-style ids so Set/has and equality work across string vs object shapes */
function normalizeId(val) {
  if (val == null || val === '') return ''
  if (typeof val === 'object') {
    if (val._id != null) return normalizeId(val._id)
    if (val.$oid != null) return String(val.$oid).trim()
    if (typeof val.toString === 'function') {
      const s = val.toString()
      if (s && s !== '[object Object]') return s.trim()
    }
  }
  return String(val).trim()
}

function idsMatch(a, b) {
  const na = normalizeId(a)
  const nb = normalizeId(b)
  if (!na || !nb) return false
  return na === nb
}

function getSalesRemarkEntryId(r) {
  if (!r || typeof r !== 'object') return ''
  return normalizeId(r._id || r.id || r.entryId || '')
}

function getReviewerRemarkEntryId(r) {
  if (!r || typeof r !== 'object') return ''
  return normalizeId(r._id || r.id || r.entryId || '')
}

/**
 * Parent sales-remark id when this reviewer remark is a reply in-thread.
 * Backend field names vary — check several aliases and nested shapes.
 */
function getReviewerReplyParentId(rm) {
  if (!rm || typeof rm !== 'object') return ''
  const raw =
    rm.parentSalesRemarkId ||
    rm.replyToSalesRemarkId ||
    rm.salesRemarkEntryId ||
    rm.salesRemarkId ||
    rm.sales_remark_id ||
    rm.salesRemark_id ||
    rm.inReplyTo ||
    rm.parentId ||
    rm.refSalesRemarkId ||
    rm.replyTo

  if (raw != null && typeof raw === 'object') {
    return normalizeId(raw._id || raw.$oid || raw.id || raw)
  }
  return normalizeId(raw)
}

function previewText(text, maxLen = 72) {
  const t = (text || '').replace(/\s+/g, ' ').trim()
  if (!t) return ''
  return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t
}

/** API may return serviceOffering as string[]; React would render items with no separator. */
function formatServiceOfferingDisplay(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ')
  }
  if (value != null && String(value).trim() !== '') {
    return String(value).trim()
  }
  return ''
}

function readPermissionsFromStorage() {
  try {
    const parsed = JSON.parse(localStorage.getItem('permissions') || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const SalesTrackerView = () => {
  const { id } = useParams()
  const { canUpdate } = useModuleAccess('sales_tracker')
  const navigate = useNavigate()
  const user = useSelector((state) => state.user)
  const userRole = user?.role || localStorage.getItem('role')
  const userPermissions = Array.isArray(user?.permissions) ? user.permissions : readPermissionsFromStorage()
  const canReplyAsReviewer = userPermissions.includes(SALES_APPROVAL_PERMISSION)
  const [sale, setSale] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [replyingToSalesRemarkId, setReplyingToSalesRemarkId] = useState(null)
  const [replyDraftText, setReplyDraftText] = useState('')
  const [isSubmittingReply, setIsSubmittingReply] = useState(false)
  const [newSalesRemarkText, setNewSalesRemarkText] = useState('')
  const [isSubmittingNewRemark, setIsSubmittingNewRemark] = useState(false)

  const formatDateToIST = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  const fetchSale = async (opts = {}) => {
    const silent = opts.silent === true
    if (!id) {
      toast.error('Sales record ID is missing', { autoClose: 2000 })
      navigate('/salesTracker')
      return
    }

    if (!silent) setIsLoading(true)
    try {
      const payload = {
        action: 'get-sales-by-id',
        id
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const row = pickSaleFromResponse(responseData)
        if (row) {
          setSale(row)
        } else {
          toast.error('Sales record not found', { autoClose: 2000 })
          navigate('/salesTracker')
        }
      } else {
        toast.error('Failed to fetch sales details', { autoClose: 2000 })
        navigate('/salesTracker')
      }
    } catch (error) {
      console.error('Fetch sales error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : 'An error occurred while fetching sales details'
      toast.error(msg, { autoClose: 2000 })
      navigate('/salesTracker')
    } finally {
      if (!silent) setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSale()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    setReplyingToSalesRemarkId(null)
    setReplyDraftText('')
    setNewSalesRemarkText('')
  }, [id])

  useEffect(() => {
    if (!canReplyAsReviewer && replyingToSalesRemarkId) {
      setReplyingToSalesRemarkId(null)
      setReplyDraftText('')
    }
  }, [canReplyAsReviewer, replyingToSalesRemarkId])

  const cancelReply = () => {
    setReplyingToSalesRemarkId(null)
    setReplyDraftText('')
  }

  const openReplyToSalesRemark = (r) => {
    const entryId = getSalesRemarkEntryId(r)
    if (!entryId) {
      toast.error('This remark cannot be replied to (missing id).', { autoClose: 2000 })
      return
    }
    setReplyingToSalesRemarkId(entryId)
    setReplyDraftText('')
  }

  const submitReviewerReply = async () => {
    if (!canReplyAsReviewer) {
      toast.error('Only users with Sales Approval permission can reply to sales remarks.', {
        autoClose: 2500
      })
      cancelReply()
      return
    }
    const entryId = replyingToSalesRemarkId
    const content = replyDraftText.trim()
    if (!entryId) return
    if (!content) {
      toast.error('Please enter a reply.', { autoClose: 2000 })
      return
    }

    const requestUserId = localStorage.getItem('id')
    const userName = localStorage.getItem('name') || ''
    if (!requestUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    const salesId = sale?._id || sale?.id || id
    if (!salesId) {
      toast.error('Sales record ID is missing.', { autoClose: 2000 })
      return
    }

    setIsSubmittingReply(true)
    try {
      const payload = {
        action: 'update-sales',
        id: String(salesId),
        requestUserId,
        reviewerRemark: {
          content,
          userId: requestUserId,
          userName: userName.trim(),
          parentSalesRemarkId: entryId
        }
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData.message || 'Reply saved.', { autoClose: 2000 })
        cancelReply()
        await fetchSale({ silent: true })
      } else {
        toast.error(responseData?.message || 'Failed to save reply.', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Reviewer reply error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : error?.message || 'An error occurred while saving the reply.'
      toast.error(msg, { autoClose: 2000 })
    } finally {
      setIsSubmittingReply(false)
    }
  }

  const submitNewSalesRemark = async () => {
    const content = newSalesRemarkText.trim()
    if (!content) {
      toast.error('Please enter a message.', { autoClose: 2000 })
      return
    }

    const requestUserId = localStorage.getItem('id')
    const userName = localStorage.getItem('name') || ''
    if (!requestUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    const salesId = sale?._id || sale?.id || id
    if (!salesId) {
      toast.error('Sales record ID is missing.', { autoClose: 2000 })
      return
    }

    setIsSubmittingNewRemark(true)
    try {
      const payload = {
        action: 'update-sales',
        id: String(salesId),
        requestUserId,
        salesRemark: {
          content,
          userId: requestUserId,
          userName: userName.trim()
        }
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData.message || 'Sales remark added.', { autoClose: 2000 })
        setNewSalesRemarkText('')
        await fetchSale({ silent: true })
      } else {
        toast.error(responseData?.message || 'Failed to add sales remark.', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Add sales remark error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : error?.message || 'An error occurred while adding the remark.'
      toast.error(msg, { autoClose: 2000 })
    } finally {
      setIsSubmittingNewRemark(false)
    }
  }

  const sid = sale?._id || sale?.id || id

  if (isLoading) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Sales</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/salesTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!sale) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Sales</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/salesTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Sales record not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const salesRemarks = Array.isArray(sale.salesRemarksHistory) ? sale.salesRemarksHistory : []
  const reviewerRemarks = Array.isArray(sale.reviewerRemarksHistory) ? sale.reviewerRemarksHistory : []

  const salesRemarksSorted = [...salesRemarks].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
  )

  const repliesForSalesRemark = (parentId) =>
    reviewerRemarks
      .filter((rm) => idsMatch(getReviewerReplyParentId(rm), parentId))
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))

  /** Reviewer rows with no parent, or parent id that does not match any current sales remark */
  const orphanReviewerRemarks = reviewerRemarks.filter((rm) => {
    const p = getReviewerReplyParentId(rm)
    if (!p) return true
    return !salesRemarks.some((sr) => idsMatch(getSalesRemarkEntryId(sr), p))
  })

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">View Sales</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/salesTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal">
                    <div className="card-body">
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Company Name</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.companyName || '-'}</div>
                        </div>
                      </div>

                     
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Contact Person</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.contactPerson || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Designation</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.designation || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Email</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.email || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Contact Number</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.contactNumber || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">City</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.city || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">State</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.state || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Priority</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.priority || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Opportunity Stage</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{sale.opportunityStage || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Service Offering</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">
                            {formatServiceOfferingDisplay(sale.serviceOffering) || '-'}
                          </div>
                        </div>
                      </div>

                      {userRole === 'admin' && (
                        <div className="form-group row align-items-center">
                          <label className="col-12 col-md-4 col-form-label">Status</label>
                          <div className="col-12 col-md-8">
                            <div className="txtdsply">
                              <span
                                className={`badge ${sale.status === 'active' ? 'badge-success' : 'badge-danger'}`}
                                style={{ fontSize: '90%', fontWeight: 550 }}
                              >
                                {sale.status || '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="row mt-2 mb-1">
                        <div className="col-12">
                          <h6 className="text-primary border-bottom pb-1 mb-1 mb-md-2">
                            <i className="fa-solid fa-comments mr-1" />
                            Sales remarks &amp; reviewer replies
                          </h6>
                          <p className="text-muted mb-2" style={{ fontSize: '12px', lineHeight: 1.35 }}>
                            New message: box below
                            {canReplyAsReviewer ? (
                              <>
                                {' '}
                                · Reviewer: <strong>Reply</strong> on a remark
                              </>
                            ) : (
                              <> · Reply: requires &quot;{SALES_APPROVAL_PERMISSION}&quot; permission</>
                            )}{' '}
                            · Refreshes after send.
                          </p>
                        </div>
                      </div>

                      <div className="form-group row mb-2">
                        <div className="col-12">
                          <div
                            className="rounded px-2 py-2 mb-2"
                            style={{
                              background: '#fff',
                              border: '1px solid #1199B2',
                              boxShadow: '0 1px 2px rgba(0,0,0,.06)'
                            }}
                          >
                            <label className="mb-1 d-block font-weight-bold text-primary" style={{ fontSize: '13px' }}>
                              <i className="fa-solid fa-plus mr-1" aria-hidden />
                              Add a sales remark
                            </label>
                            <textarea
                              className="form-control form-control-sm mb-1"
                              rows={2}
                              value={newSalesRemarkText}
                              onChange={(e) => setNewSalesRemarkText(e.target.value)}
                              disabled={isSubmittingNewRemark}
                              placeholder="Type a new sales message…"
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-blue"
                              onClick={submitNewSalesRemark}
                              disabled={isSubmittingNewRemark}
                            >
                              {isSubmittingNewRemark ? 'Sending…' : 'Send remark'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {salesRemarksSorted.length === 0 && orphanReviewerRemarks.length === 0 ? (
                        <div className="form-group row">
                          <div className="col-12">
                            <div className="txtdsply text-muted small">
                              No messages in the thread yet. Add one with the box above.
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="mb-2 p-2 rounded"
                          style={{
                            background: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            maxHeight: 'min(58vh, 440px)',
                            overflowY: 'auto'
                          }}
                        >
                          {salesRemarksSorted.map((r) => {
                            const remarkEntryId = getSalesRemarkEntryId(r)
                            const isReplyOpen = remarkEntryId && replyingToSalesRemarkId === remarkEntryId
                            const nested = remarkEntryId ? repliesForSalesRemark(remarkEntryId) : []

                            return (
                              <div
                                key={r._id || r.id || r.entryId || r.content}
                                className="mb-2"
                                style={{ maxWidth: '100%' }}
                              >
                                <div
                                  className="rounded px-2 py-2"
                                  style={{
                                    background: '#fff',
                                    borderLeft: '3px solid #1199B2',
                                    boxShadow: '0 1px 2px rgba(0,0,0,.06)'
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                                    <div
                                      className="text-muted mb-0 pr-2"
                                      style={{ flex: '1 1 auto', fontSize: '12px', lineHeight: 1.3 }}
                                    >
                                      <strong className="text-dark">
                                        {r.createdByName || displayUser(r.createdBy)}
                                      </strong>
                                      <span className="mx-1">·</span>
                                      <span>{formatDateToIST(r.createdAt)}</span>
                                    </div>
                                    {remarkEntryId && canReplyAsReviewer ? (
                                      <div className="mb-0" style={{ lineHeight: 1.2 }}>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-link p-0 text-primary"
                                          title="Reply as reviewer (Sales Approval)"
                                          onClick={() => openReplyToSalesRemark(r)}
                                        >
                                          <i className="fa-solid fa-reply mr-1" />
                                          Reply
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>

                                  <div
                                    className="mt-1"
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      fontSize: '13px',
                                      lineHeight: 1.35
                                    }}
                                  >
                                    {r.content || '-'}
                                  </div>

                                  {nested.length > 0 && (
                                    <div className="mt-2 pl-2" style={{ borderLeft: '2px solid #ced4da' }}>
                                      {nested.map((rm) => (
                                        <div
                                          key={getReviewerRemarkEntryId(rm) || rm.content}
                                          className="rounded px-2 py-1 mb-1"
                                          style={{
                                            background: '#e9ecef',
                                            borderLeft: '2px solid #6c757d'
                                          }}
                                        >
                                          <div
                                            className="text-muted mb-0"
                                            style={{ fontSize: '11px', lineHeight: 1.3 }}
                                          >
                                            <strong className="text-dark">
                                              {rm.createdByName || displayUser(rm.createdBy)}
                                            </strong>
                                            <span className="text-muted"> (reviewer)</span>
                                            <span className="mx-1">·</span>
                                            <span>{formatDateToIST(rm.createdAt)}</span>
                                            <span className="text-muted"> · </span>
                                            <span className="font-italic" style={{ color: '#5a6268' }}>
                                              ↳ {previewText(r.content, 48) || '…'}
                                            </span>
                                          </div>
                                          <div
                                            style={{
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-word',
                                              fontSize: '13px',
                                              lineHeight: 1.35
                                            }}
                                          >
                                            {rm.content || '-'}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {isReplyOpen && canReplyAsReviewer && (
                                    <div className="mt-2 pt-1 border-top">
                                      <label className="text-muted d-block mb-1" style={{ fontSize: '12px' }}>
                                        Your reply
                                      </label>
                                      <textarea
                                        className="form-control form-control-sm mb-1"
                                        rows={2}
                                        value={replyDraftText}
                                        onChange={(e) => setReplyDraftText(e.target.value)}
                                        disabled={isSubmittingReply}
                                        placeholder="Write a reviewer reply…"
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-blue mr-1"
                                        onClick={submitReviewerReply}
                                        disabled={isSubmittingReply}
                                      >
                                        {isSubmittingReply ? 'Sending…' : 'Send reply'}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={cancelReply}
                                        disabled={isSubmittingReply}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })}

                          {orphanReviewerRemarks.length > 0 && (
                            <div className="mt-1 pt-2 border-top">
                              <div className="text-muted font-weight-bold mb-1" style={{ fontSize: '12px' }}>
                                Other reviewer remarks
                              </div>
                              <p className="text-muted mb-1" style={{ fontSize: '11px', lineHeight: 1.35 }}>
                                Not nested: no parent id, or parent id does not match a sales remark (check API{' '}
                                <code>parentSalesRemarkId</code>).
                              </p>
                              {orphanReviewerRemarks.map((rm) => {
                                const parentRef = getReviewerReplyParentId(rm)
                                return (
                                  <div
                                    key={getReviewerRemarkEntryId(rm) || rm.content}
                                    className="rounded px-2 py-1 mb-1"
                                    style={{ background: '#e9ecef', borderLeft: '2px solid #6c757d' }}
                                  >
                                    <div className="text-muted mb-0" style={{ fontSize: '11px', lineHeight: 1.3 }}>
                                      <strong className="text-dark">
                                        {rm.createdByName || displayUser(rm.createdBy)}
                                      </strong>
                                      <span className="text-muted"> (reviewer)</span>
                                      <span className="mx-1">·</span>
                                      <span>{formatDateToIST(rm.createdAt)}</span>
                                    </div>
                                    {!parentRef ? (
                                      <div className="mb-0" style={{ color: '#6c757d', fontSize: '11px' }}>
                                        <i className="fa-solid fa-link-slash mr-1" aria-hidden />
                                        No parent id.
                                      </div>
                                    ) : (
                                      <div className="mb-0 text-warning" style={{ fontSize: '11px', lineHeight: 1.35 }}>
                                        <i className="fa-solid fa-exclamation-triangle mr-1" aria-hidden />
                                        Parent <code>{parentRef}</code> not in thread — check <code>get-sales-by-id</code>.
                                      </div>
                                    )}
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {rm.content || '-'}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Created By</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{displayUser(sale.createdBy)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Created At</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDateToIST(sale.createdAt)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Updated By</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{displayUser(sale.updatedBy)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Updated At</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDateToIST(sale.updatedAt)}</div>
                        </div>
                      </div>
                    </div>

                    {canUpdate && (
                      <div className="card-footer text-center">
                        <Link to={`/salesTrackerEdit/${sid}`} className="btn btn-blue mr-2">
                          Edit
                        </Link>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SalesTrackerView
